import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  IAnalyticsDashboard,
  IAnalyticsWidget,
  IMLModel,
  IMLConfig,
  IDashboardLayout,
  IWidgetConfig,
  IWidgetPosition,
  IWidgetSize,
  AnalyticsMetrics,
  IUserBehaviorAnalytics,
  IAnalyticsFilter,
  IDataSource,
  IReportConfig,
  MLModelStatus,
  AnalyticsWidgetType,
  ChartType,
  DataSourceType,
  ConnectionType,
  MLModelType,
  MLAlgorithm,
  IDateRange,
  ReportFormat,
  ReportType,
  DateRangePeriod,
  UserAnalyticsEntry,
  UserAnalyticsFilters,
  UserAnalyticsMetrics,
  UserAnalyticsPagination,
  UserSegmentation,
} from "../types/analytics";
import analyticsMlApi from "../services/api/analytics-ml.api";
import {
  dashboardAnalyticsApi,
  type DashboardMetrics as DashboardAnalyticsMetrics,
  type DashboardFilters as DashboardAnalyticsFilters,
} from "../services/api/dashboard-analytics.api";
import { logger } from "../utils/logger";
import usersAnalyticsApi, {
  UserAnalytics,
} from "../services/api/users-analytics.api";
import { AnalyticsTimeGranularity } from "../types/enums/analytics-enums";

// Re-export domain thunks from dedicated slices for backward compatibility
export {
  fetchParticipantAnalytics,
  fetchParticipantMetrics,
  fetchParticipantSegmentation,
} from "./participantsAnalyticsSlice";
export { fetchEventAnalytics, fetchEventMetrics } from "./eventsAnalyticsSlice";
export {
  fetchConversionFunnel,
  fetchConversionMetrics,
} from "./conversionsAnalyticsSlice";

interface AnalyticsState {
  // Дашборды и виджеты
  dashboards: IAnalyticsDashboard[];
  currentDashboard: IAnalyticsDashboard | null;
  widgets: IAnalyticsWidget[];
  selectedWidget: IAnalyticsWidget | null;

  // Источники данных
  dataSources: IDataSource[];
  activeDataSource: IDataSource | null;

  // Метрики и аналитика
  metrics: AnalyticsMetrics | null;
  userBehavior: IUserBehaviorAnalytics[];
  realTimeData: Record<string, unknown>;

  // Фильтры и настройки
  globalFilters: IAnalyticsFilter[];
  dateRange: { start: Date; end: Date };
  autoRefresh: boolean;
  refreshInterval: number;
  usersAnalytics: {
    items: UserAnalyticsEntry[];
    metrics: UserAnalyticsMetrics | null;
    segmentation: UserSegmentation | null;
    filters: UserAnalyticsFilters;
    pagination: UserAnalyticsPagination | null;
  };

  // Тренды дашборда (Q3)
  dashboardTrends: {
    users: Array<{ date: string; count: number }>;
    events: Array<{ date: string; count: number }>;
    revenue: Array<{ date: string; amount: number }>;
    engagement: Array<{ date: string; score: number }>;
  } | null;
  loadingTrends: boolean;

  // Отчеты
  reports: IReportConfig[];
  generatingReport: boolean;

  // UI состояние
  loading: {
    dashboards: boolean;
    widgets: boolean;
    data: boolean;
    reports: boolean;
    users: boolean;
    userSegmentation: boolean;
    performance: boolean;
  };
  error: string | null;

  // Настройки
  settings: {
    theme: "light" | "dark";
    animations: boolean;
    gridSize: number;
    autoSave: boolean;
    mlEnabled: boolean;
  };
}

const initialState: AnalyticsState = {
  dashboards: [],
  currentDashboard: null,
  widgets: [],
  selectedWidget: null,

  dataSources: [],
  activeDataSource: null,

  metrics: null,
  userBehavior: [],
  realTimeData: {},

  globalFilters: [],
  dateRange: {
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 дней назад
    end: new Date(),
  },
  autoRefresh: true,
  refreshInterval: 60000, // 1 минута
  usersAnalytics: {
    items: [],
    metrics: null,
    segmentation: null,
    filters: {},
    pagination: null,
  },

  dashboardTrends: null,
  loadingTrends: false,

  reports: [],
  generatingReport: false,

  loading: {
    dashboards: false,
    widgets: false,
    data: false,
    reports: false,
    users: false,
    userSegmentation: false,
    performance: false,
  },
  error: null,

  settings: {
    theme: "light",
    animations: true,
    gridSize: 12,
    autoSave: true,
    mlEnabled: true,
  },
};

export const parseDate = (
  value?: Date | string | number | null,
  fallback?: Date,
): Date => {
  if (value instanceof Date) {
    return value;
  }

  if (value === undefined || value === null) {
    return fallback ? new Date(fallback) : new Date();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback ? new Date(fallback) : new Date();
  }

  return parsed;
};

export const parseOptionalDate = (
  value?: Date | string | number | null,
): Date | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const parsed = parseDate(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const normalizeDateRange = (range?: IDateRange): IDateRange => {
  if (!range) {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      start,
      end,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    };
  }

  return {
    ...range,
    start: parseDate(range.start),
    end: parseDate(range.end),
    timezone:
      range.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "UTC",
  };
};

const normalizeDataSource = (
  source?: Partial<IDataSource> | null,
): IDataSource => {
  const conn = source?.connection;
  const schema = source?.schema;
  return {
    id: String(source?.id ?? `ds-${Date.now()}`),
    name: String(source?.name ?? "Unknown Data Source"),
    type: ensureEnumValue(
      source?.type,
      DATA_SOURCE_TYPES,
      "api",
    ) as DataSourceType,
    connection: {
      type: ensureEnumValue(
        conn?.type,
        CONNECTION_TYPES_VALUES,
        "rest_api",
      ) as ConnectionType,
      ...(conn?.host !== undefined ? { host: conn.host } : {}),
      ...(conn?.port !== undefined ? { port: conn.port } : {}),
      ...(conn?.database !== undefined ? { database: conn.database } : {}),
      ...(conn?.username !== undefined ? { username: conn.username } : {}),
      ...(conn?.apiUrl !== undefined ? { apiUrl: conn.apiUrl } : {}),
      ...(conn?.apiKey !== undefined ? { apiKey: conn.apiKey } : {}),
      ...(conn?.headers !== undefined ? { headers: conn.headers } : {}),
    },
    query: String(source?.query ?? ""),
    parameters: source?.parameters ?? {},
    refreshInterval: Number(source?.refreshInterval ?? 0),
    lastUpdated: parseDate(source?.lastUpdated),
    schema: {
      columns: schema?.columns ?? [],
      indexes: schema?.indexes ?? [],
      relationships: schema?.relationships ?? [],
    },
  };
};

export const normalizeModel = (model: unknown): IMLModel => {
  const m = model as Partial<IMLModel>;
  return {
    id: String(m?.id ?? `ml-${Date.now()}`),
    name: String(m?.name ?? "Unnamed model"),
    type: ensureEnumValue(
      m?.type,
      ML_MODEL_TYPE_VALUES,
      "classification",
    ) as MLModelType,
    algorithm: ensureEnumValue(
      m?.algorithm,
      ML_ALGORITHM_VALUES,
      "random_forest",
    ) as MLAlgorithm,
    accuracy: Number(m?.accuracy ?? 0),
    lastTrained: parseDate(m?.lastTrained),
    // deployedAt в IMLModel является обязательным, поэтому задаём безопасное значение по умолчанию
    deployedAt: parseOptionalDate(m?.deployedAt) ?? new Date(0),
    features: m?.features ?? [],
    predictions: m?.predictions ?? [],
    config: m?.config ?? ({} as IMLConfig),
    status: ensureEnumValue(
      m?.status,
      ML_MODEL_STATUS_VALUES,
      "trained",
    ) as MLModelStatus,
  };
};

const normalizeWidget = (widget: unknown): IAnalyticsWidget => {
  const w = widget as Partial<IAnalyticsWidget>;
  const base: IAnalyticsWidget = {
    id: String(w?.id ?? `widget-${Date.now()}`),
    type: ensureEnumValue(
      w?.type,
      ANALYTICS_WIDGET_TYPES,
      "custom",
    ) as AnalyticsWidgetType,
    title: String(w?.title ?? "Новый виджет"),
    chartType: ensureEnumValue(w?.chartType, CHART_TYPES, "line") as ChartType,
    dataSource: normalizeDataSource(w?.dataSource),
    config: w?.config ?? ({} as IWidgetConfig),
    position: w?.position ?? ({} as IWidgetPosition),
    size: w?.size ?? ({} as IWidgetSize),
    filters: w?.filters ?? [],
    drillDownEnabled: Boolean(w?.drillDownEnabled ?? false),
    realTimeEnabled: Boolean(w?.realTimeEnabled ?? false),
    mlEnabled: Boolean(w?.mlEnabled ?? false),
  };

  if (w?.description !== undefined && w.description !== null) {
    base.description = String(w.description);
  }
  if (w?.mlModel !== undefined && w.mlModel !== null) {
    base.mlModel = normalizeModel(w.mlModel);
  }

  return base;
};

const normalizeDashboard = (dashboard: unknown): IAnalyticsDashboard => {
  const d = dashboard as Partial<IAnalyticsDashboard>;
  const base: IAnalyticsDashboard = {
    id: String(d?.id ?? `dashboard-${Date.now()}`),
    name: String(d?.name ?? "Новый дашборд"),
    widgets: (d?.widgets ?? []).map(normalizeWidget),
    layout:
      d?.layout ??
      ({
        columns: 12,
        rowHeight: 150,
        margin: [10, 10] as [number, number],
        containerPadding: [10, 10] as [number, number],
        responsive: true,
        breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480 },
      } as IDashboardLayout),
    filters: d?.filters ?? [],
    dateRange: normalizeDateRange(d?.dateRange),
    refreshInterval: Number(d?.refreshInterval ?? 60000),
    isPublic: Boolean(d?.isPublic ?? false),
    ownerId: String(d?.ownerId ?? ""),
    createdAt: parseDate(d?.createdAt),
    updatedAt: parseDate(d?.updatedAt),
  };

  if (d?.description !== undefined && d.description !== null) {
    base.description = String(d.description);
  }

  return base;
};

const mapDashboardToApi = (
  dashboard: Partial<IAnalyticsDashboard>,
): Partial<IAnalyticsDashboard> => ({
  ...(dashboard.id !== undefined ? { id: dashboard.id } : {}),
  ...(dashboard.name !== undefined ? { name: dashboard.name } : {}),
  ...(dashboard.description !== undefined
    ? { description: dashboard.description }
    : {}),
  ...(dashboard.layout !== undefined ? { layout: dashboard.layout } : {}),
  ...(dashboard.filters !== undefined ? { filters: dashboard.filters } : {}),
  ...(dashboard.dateRange !== undefined
    ? { dateRange: dashboard.dateRange }
    : {}),
  ...(dashboard.refreshInterval !== undefined
    ? { refreshInterval: dashboard.refreshInterval }
    : {}),
  ...(dashboard.isPublic !== undefined ? { isPublic: dashboard.isPublic } : {}),
  ...(dashboard.ownerId !== undefined ? { ownerId: dashboard.ownerId } : {}),
  ...(dashboard.createdAt !== undefined
    ? { createdAt: dashboard.createdAt }
    : {}),
  ...(dashboard.updatedAt !== undefined
    ? { updatedAt: dashboard.updatedAt }
    : {}),
});

const mapWidgetToApi = (
  widget: Partial<IAnalyticsWidget>,
): Partial<IAnalyticsWidget> => ({
  ...(widget.id !== undefined ? { id: widget.id } : {}),
  ...(widget.type !== undefined ? { type: widget.type } : {}),
  ...(widget.title !== undefined ? { title: widget.title } : {}),
  ...(widget.description !== undefined
    ? { description: widget.description }
    : {}),
  ...(widget.chartType !== undefined ? { chartType: widget.chartType } : {}),
  ...(widget.dataSource !== undefined ? { dataSource: widget.dataSource } : {}),
  ...(widget.config !== undefined ? { config: widget.config } : {}),
  ...(widget.position !== undefined ? { position: widget.position } : {}),
  ...(widget.size !== undefined ? { size: widget.size } : {}),
  ...(widget.filters !== undefined ? { filters: widget.filters } : {}),
  ...(widget.drillDownEnabled !== undefined
    ? { drillDownEnabled: widget.drillDownEnabled }
    : {}),
  ...(widget.realTimeEnabled !== undefined
    ? { realTimeEnabled: widget.realTimeEnabled }
    : {}),
  ...(widget.mlEnabled !== undefined ? { mlEnabled: widget.mlEnabled } : {}),
  ...(widget.mlModel !== undefined ? { mlModel: widget.mlModel } : {}),
});

export const mapModelToApi = (model: Partial<IMLModel>): Partial<IMLModel> => ({
  ...(model.id !== undefined ? { id: model.id } : {}),
  ...(model.name !== undefined ? { name: model.name } : {}),
  ...(model.type !== undefined ? { type: model.type } : {}),
  ...(model.algorithm !== undefined ? { algorithm: model.algorithm } : {}),
  ...(model.accuracy !== undefined ? { accuracy: model.accuracy } : {}),
  ...(model.lastTrained !== undefined
    ? { lastTrained: model.lastTrained }
    : {}),
  ...(model.deployedAt !== undefined ? { deployedAt: model.deployedAt } : {}),
  ...(model.features !== undefined ? { features: model.features } : {}),
  ...(model.predictions !== undefined
    ? { predictions: model.predictions }
    : {}),
  ...(model.config !== undefined ? { config: model.config } : {}),
  ...(model.status !== undefined ? { status: model.status } : {}),
});

const mapAnalyticsFiltersToFields = (
  filters: IAnalyticsFilter[] = [],
): string[] =>
  filters
    .map((filter) => filter.field)
    .filter((field): field is string => Boolean(field));

const mapDashboardMetricsToAnalyticsMetrics = (
  metrics: DashboardAnalyticsMetrics,
): AnalyticsMetrics => {
  const totalRevenue = metrics.financial?.totalRevenue ?? 0;
  const totalEvents = metrics.overview?.totalEvents ?? 0;
  const totalUsers = metrics.overview?.totalUsers ?? 0;
  const activeUsers = metrics.overview?.activeUsers ?? 0;

  const eventsTrend = metrics.trends?.eventsTrend ?? [];
  const usersTrend = metrics.trends?.usersTrend ?? [];
  const revenueTrend = metrics.trends?.revenueTrend ?? [];

  const lastEventsValue =
    eventsTrend[eventsTrend.length - 1]?.count ?? totalEvents;
  const lastUsersValue = usersTrend[usersTrend.length - 1]?.count ?? totalUsers;
  const firstRevenueValue = revenueTrend[0]?.amount ?? 0;
  const firstEventsValue = eventsTrend[0]?.count ?? 0;
  const firstUsersValue = usersTrend[0]?.count ?? 0;
  const lastRevenueValue =
    revenueTrend[revenueTrend.length - 1]?.amount ?? totalRevenue;

  return {
    totalRevenue,
    totalEvents,
    totalUsers,
    activeUsers,
    totalOrders: metrics.financial?.topRevenueSources?.length ?? 0,
    customerSatisfaction: metrics.engagement?.averageRating ?? 0,
    marketGrowth: metrics.financial?.revenueGrowth ?? 0,
    currentPeriod: {
      revenue: lastRevenueValue,
      events: lastEventsValue,
      users: lastUsersValue,
    },
    previousPeriod: {
      revenue: firstRevenueValue,
      events: firstEventsValue,
      users: firstUsersValue,
    },
    growth: {
      revenue: metrics.financial?.revenueGrowth ?? 0,
      events: metrics.overview?.newEvents ?? 0,
      users: metrics.overview?.newUsers ?? 0,
    },
    conversionRate: metrics.performance?.conversionRate ?? 0,
    engagementGrowth: metrics.engagement?.retentionRate ?? 0,
  };
};

const mapReportFormatToApiType = (
  format: ReportFormat,
): "pdf" | "excel" | "csv" => {
  switch (format) {
    case ReportFormat.EXCEL:
      return "excel";
    case ReportFormat.CSV:
      return "csv";
    default:
      return "pdf";
  }
};

const mapReportTypeToApiFormat = (
  type: ReportType,
): "summary" | "detailed" | "custom" => {
  switch (type) {
    case ReportType.DETAILED:
      return "detailed";
    case ReportType.CUSTOM:
      return "custom";
    default:
      return "summary";
  }
};

const ANALYTICS_WIDGET_TYPES = [
  "metric",
  "chart",
  "table",
  "map",
  "funnel",
  "cohort",
  "heatmap",
  "prediction",
  "recommendation",
  "custom",
] as const;

const CHART_TYPES = [
  "line",
  "bar",
  "pie",
  "doughnut",
  "area",
  "scatter",
  "bubble",
  "radar",
  "treemap",
  "sankey",
  "waterfall",
  "candlestick",
] as const;

const DATA_SOURCE_TYPES = [
  "api",
  "database",
  "file",
  "stream",
  "webhook",
  "manual",
] as const;

const CONNECTION_TYPES_VALUES = [
  "rest_api",
  "graphql",
  "postgresql",
  "mysql",
  "mongodb",
  "redis",
  "elasticsearch",
] as const;

const ML_MODEL_TYPE_VALUES = [
  "classification",
  "regression",
  "clustering",
  "anomaly_detection",
  "time_series",
  "nlp",
  "recommendation",
  "forecasting",
] as const;

const ML_ALGORITHM_VALUES = [
  "linear_regression",
  "logistic_regression",
  "random_forest",
  "gradient_boosting",
  "svm",
  "neural_network",
  "k_means",
  "dbscan",
  "isolation_forest",
  "arima",
  "lstm",
  "transformer",
] as const;

const ML_MODEL_STATUS_VALUES = [
  "training",
  "trained",
  "deploying",
  "deployed",
  "error",
  "deprecated",
] as const;

export const ensureEnumValue = <T extends readonly string[]>(
  value: unknown,
  allowed: T,
  fallback: T[number],
): T[number] => {
  if (
    typeof value === "string" &&
    (allowed as readonly string[]).includes(value)
  ) {
    return value as T[number];
  }
  return fallback;
};

/**
 * AD-93. Нормализатор подставлял значения там, где сервер НИЧЕГО не присылал:
 * два десятка полей получали ноль, отдел — прочерк, а дата последнего входа —
 * текущий момент. Экран печатал это как измеренные величины. Осталось только
 * то, что есть в ответе; отсутствие даты входа теперь `null`, и подпись «не
 * входил» подставляет экран, а не хранилище.
 */
const normalizeUserAnalyticsEntry = (
  entry: Partial<UserAnalytics>,
): UserAnalyticsEntry => ({
  id: String(entry?.id ?? `user-${Date.now()}`),
  name: String(entry?.name ?? ""),
  email: String(entry?.email ?? ""),
  avatar: entry?.avatar ? String(entry.avatar) : "",
  role: String(entry?.role ?? ""),
  department: String(entry?.department ?? ""),
  status: (entry?.status ?? "active") as UserAnalyticsEntry["status"],
  registrationDate: String(entry?.registrationDate ?? ""),
  lastLoginDate: entry?.lastLoginDate ? String(entry.lastLoginDate) : null,
  totalEvents: Number(entry?.totalEvents ?? 0),
  eventsAttended: Number(entry?.eventsAttended ?? 0),
  averageRating: Number(entry?.averageRating ?? 0),
  engagementScore: Number(entry?.engagementScore ?? 0),
  activityLevel: (entry?.activityLevel ??
    "low") as UserAnalyticsEntry["activityLevel"],
});

/**
 * AD-93. Прежний вариант читал шесть разделов (`overview`, `engagement`,
 * `performance`, `social`, `financial`, `trends`), которых в ответе нет:
 * каждое обращение попадало в undefined, и все карточки экрана показывали ноль
 * на полной базе. Сервер отдаёт плоский набор — он здесь и разбирается.
 */
const normalizeUserMetrics = (
  metrics: Partial<UserAnalyticsMetrics>,
): UserAnalyticsMetrics => ({
  totalUsers: Number(metrics?.totalUsers ?? 0),
  activeUsers: Number(metrics?.activeUsers ?? 0),
  inactiveUsers: Number(metrics?.inactiveUsers ?? 0),
  blockedUsers: Number(metrics?.blockedUsers ?? 0),
  newUsers: Number(metrics?.newUsers ?? 0),
  averageEventsPerUser: Number(metrics?.averageEventsPerUser ?? 0),
  averageRating: Number(metrics?.averageRating ?? 0),
  averageAttendanceRate: Number(metrics?.averageAttendanceRate ?? 0),
  averageEngagement: Number(metrics?.averageEngagement ?? 0),
  topDepartments: (metrics?.topDepartments ?? []).map((item) => ({
    department: String(item?.department ?? ""),
    count: Number(item?.count ?? 0),
    percentage: Number(item?.percentage ?? 0),
  })),
  topRoles: (metrics?.topRoles ?? []).map((item) => ({
    role: String(item?.role ?? ""),
    count: Number(item?.count ?? 0),
    percentage: Number(item?.percentage ?? 0),
  })),
});

const normalizeUserSegmentation = (
  segmentation: Partial<UserSegmentation> | null | undefined,
): UserSegmentation | null => {
  if (!segmentation) {
    return null;
  }
  return {
    byRole: (segmentation?.byRole ?? []).map((item) => ({
      role: String(item?.role ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
    byDepartment: (segmentation?.byDepartment ?? []).map((item) => ({
      department: String(item?.department ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
    // AD-93: поле ступени называется `level`. Чтение `activityLevel` давало
    // undefined, и подпись столбика была пустой.
    byActivity: (segmentation?.byActivity ?? []).map((item) => ({
      level: String(item?.level ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
    // AD-93: `byPerformance`/`byEngagement` сервер не отдаёт, а `byStatus` —
    // отдаёт, и он единственный, который экран не показывал.
    byStatus: (segmentation?.byStatus ?? []).map((item) => ({
      status: String(item?.status ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
  };
};

const normalizeUserFilters = (
  filters: UserAnalyticsFilters = {},
): UserAnalyticsFilters => ({
  dateRange: filters.dateRange ?? DateRangePeriod.LAST_30,
  role: filters.role ?? "",
  department: filters.department ?? "",
  status: filters.status ?? "",
  activityLevel: filters.activityLevel ?? "",
  page: filters.page ?? 1,
  limit: filters.limit ?? 10,
});

const normalizeUserPagination = (
  pagination: Partial<UserAnalyticsPagination> | null | undefined,
): UserAnalyticsPagination | null => {
  if (!pagination) {
    return null;
  }
  return {
    page: Number(pagination?.page ?? 1),
    limit: Number(pagination?.limit ?? 10),
    total: Number(pagination?.total ?? 0),
    totalPages: Number(pagination?.totalPages ?? 0),
  };
};

// Async Thunks для работы с API

// Дашборды
export const fetchDashboards = createAsyncThunk(
  "analytics/fetchDashboards",
  async (userId?: string) => {
    try {
      const dashboards = await analyticsMlApi.getDashboards(
        userId ? { userId } : undefined,
      );
      return dashboards.map(normalizeDashboard);
    } catch (error) {
      logger.error("Не удалось загрузить дашборды аналитики", error);
      throw error;
    }
  },
);

export const createDashboard = createAsyncThunk(
  "analytics/createDashboard",
  async (dashboardData: Partial<IAnalyticsDashboard>) => {
    try {
      const apiDashboard = mapDashboardToApi(dashboardData);
      const dashboard = await analyticsMlApi.createDashboard(apiDashboard);
      return normalizeDashboard(dashboard);
    } catch (error) {
      logger.error("Не удалось создать дашборд аналитики", error);
      throw error;
    }
  },
);

// Виджеты
export const fetchWidgets = createAsyncThunk(
  "analytics/fetchWidgets",
  async (dashboardId: string) => {
    try {
      const widgets = await analyticsMlApi.getWidgets({ dashboardId });
      return widgets.map(normalizeWidget);
    } catch (error) {
      logger.error("Не удалось загрузить виджеты аналитики", error);
      throw error;
    }
  },
);

export const createWidget = createAsyncThunk(
  "analytics/createWidget",
  async (widgetData: Partial<IAnalyticsWidget>) => {
    try {
      const apiWidget = mapWidgetToApi(widgetData);
      const widget = await analyticsMlApi.createWidget(apiWidget);
      return normalizeWidget(widget);
    } catch (error) {
      logger.error("Не удалось создать виджет аналитики", error);
      throw error;
    }
  },
);

// Метрики
export const fetchMetrics = createAsyncThunk(
  "analytics/fetchMetrics",
  async (filters: IAnalyticsFilter[] = []) => {
    try {
      const params: DashboardAnalyticsFilters = {};
      const metricFields = mapAnalyticsFiltersToFields(filters);

      if (metricFields.length) {
        params.metrics = metricFields;
      }

      const response = await dashboardAnalyticsApi.getDashboardMetrics(params);
      return mapDashboardMetricsToAnalyticsMetrics(response.data);
    } catch (error) {
      logger.error("Не удалось получить метрики аналитики", error);
      throw error;
    }
  },
);

// Q3/Q4: unified period maps
const GRANULARITY_TO_PERIOD: Record<AnalyticsTimeGranularity, string> = {
  [AnalyticsTimeGranularity.DAY]: "7",
  [AnalyticsTimeGranularity.WEEK]: "30",
  [AnalyticsTimeGranularity.MONTH]: "90",
  [AnalyticsTimeGranularity.QUARTER]: "365",
  [AnalyticsTimeGranularity.YEAR]: "365",
};

export const GRANULARITY_TO_PRESET: Record<
  AnalyticsTimeGranularity,
  DashboardAnalyticsFilters["preset"]
> = {
  [AnalyticsTimeGranularity.DAY]: "last_7_days",
  [AnalyticsTimeGranularity.WEEK]: "last_30_days",
  [AnalyticsTimeGranularity.MONTH]: "last_90_days",
  [AnalyticsTimeGranularity.QUARTER]: "last_year",
  [AnalyticsTimeGranularity.YEAR]: "last_year",
};

// Q3: Тренды дашборда с гранулярностью
export const fetchDashboardTrends = createAsyncThunk(
  "analytics/fetchDashboardTrends",
  async (
    granularity: AnalyticsTimeGranularity = AnalyticsTimeGranularity.DAY,
  ) => {
    try {
      const period = GRANULARITY_TO_PERIOD[granularity];
      const response = await dashboardAnalyticsApi.getTrends({
        dateRange: period,
      });
      return response.data;
    } catch (error) {
      logger.error("Не удалось загрузить тренды дашборда", error);
      throw error;
    }
  },
);

interface UsersApiShape {
  users?: UserAnalytics[];
  pagination?: Partial<UserAnalyticsPagination>;
  filters?: UserAnalyticsFilters;
}
interface UsersApiPayload extends UsersApiShape {
  data?: UsersApiShape;
}

export const fetchUserAnalytics = createAsyncThunk(
  "analytics/fetchUserAnalytics",
  async (filters: UserAnalyticsFilters = {}) => {
    try {
      const response = await usersAnalyticsApi.getUsers(filters);
      const payload = response.data as UsersApiPayload;
      const result = payload?.data ?? payload;

      const users: UserAnalytics[] = result?.users ?? [];
      const pagination = result?.pagination ?? payload?.pagination ?? null;
      const responseFilters = result?.filters ?? payload?.filters ?? filters;

      return {
        users: users.map(normalizeUserAnalyticsEntry),
        pagination: normalizeUserPagination(pagination),
        filters: normalizeUserFilters(responseFilters),
      };
    } catch (error) {
      logger.error("Не удалось загрузить аналитику пользователей", error);
      throw error;
    }
  },
);

export const fetchUserMetrics = createAsyncThunk(
  "analytics/fetchUserMetrics",
  async (filters: UserAnalyticsFilters = {}) => {
    try {
      const response = await usersAnalyticsApi.getUsersMetrics(filters);
      return normalizeUserMetrics(
        response.data as Partial<UserAnalyticsMetrics>,
      );
    } catch (error) {
      logger.error("Не удалось получить метрики пользователей", error);
      throw error;
    }
  },
);

export const fetchUserSegmentation = createAsyncThunk(
  "analytics/fetchUserSegmentation",
  async (filters: UserAnalyticsFilters = {}) => {
    try {
      const response = await usersAnalyticsApi.getUsersSegmentation(filters);
      return normalizeUserSegmentation(
        response.data as Partial<UserSegmentation>,
      );
    } catch (error) {
      logger.error("Не удалось получить сегментацию пользователей", error);
      throw error;
    }
  },
);

// Отчеты
export const generateReport = createAsyncThunk(
  "analytics/generateReport",
  async (reportConfig: IReportConfig) => {
    try {
      const response = await dashboardAnalyticsApi.generateReport({
        type: mapReportFormatToApiType(reportConfig.format),
        format: mapReportTypeToApiFormat(reportConfig.type),
        dateRange: "custom",
        includeCharts: true,
        includeMLInsights: true,
      });

      return response.data;
    } catch (error) {
      logger.error("Не удалось сгенерировать отчет аналитики", error);
      throw error;
    }
  },
);

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    // Дашборды
    setCurrentDashboard: (
      state,
      action: PayloadAction<IAnalyticsDashboard | null>,
    ) => {
      state.currentDashboard = action.payload;
    },

    updateDashboard: (
      state,
      action: PayloadAction<Partial<IAnalyticsDashboard> & { id: string }>,
    ) => {
      const index = state.dashboards.findIndex(
        (d) => d.id === action.payload.id,
      );
      if (index === -1) return;

      const existing = state.dashboards[index];
      if (!existing) return;

      const {
        description,
        name,
        widgets,
        layout,
        filters,
        dateRange,
        refreshInterval,
        isPublic,
        ...rest
      } = action.payload;

      const updated: IAnalyticsDashboard = {
        ...existing,
        ...rest,
        name: name ?? existing.name,
        ...(description !== undefined ? { description } : {}),
        ...(widgets !== undefined ? { widgets } : {}),
        ...(layout !== undefined ? { layout } : {}),
        ...(filters !== undefined ? { filters } : {}),
        ...(dateRange !== undefined ? { dateRange } : {}),
        ...(refreshInterval !== undefined ? { refreshInterval } : {}),
        ...(isPublic !== undefined ? { isPublic } : {}),
      };

      state.dashboards[index] = updated;

      if (state.currentDashboard?.id === action.payload.id) {
        state.currentDashboard = updated;
      }
    },

    deleteDashboard: (state, action: PayloadAction<string>) => {
      state.dashboards = state.dashboards.filter(
        (d) => d.id !== action.payload,
      );
      if (state.currentDashboard?.id === action.payload) {
        state.currentDashboard = null;
      }
    },

    // Виджеты
    setSelectedWidget: (
      state,
      action: PayloadAction<IAnalyticsWidget | null>,
    ) => {
      state.selectedWidget = action.payload;
    },

    updateWidget: (
      state,
      action: PayloadAction<Partial<IAnalyticsWidget> & { id: string }>,
    ) => {
      const index = state.widgets.findIndex((w) => w.id === action.payload.id);
      if (index === -1) return;

      const existing = state.widgets[index];
      if (!existing) return;

      const {
        description,
        mlModel,
        type,
        title,
        chartType,
        dataSource,
        config,
        position,
        size,
        filters,
        drillDownEnabled,
        realTimeEnabled,
        mlEnabled,
        ...rest
      } = action.payload;

      const updated: IAnalyticsWidget = {
        ...existing,
        ...rest,
        type: type ?? existing.type,
        ...(title !== undefined ? { title } : {}),
        ...(chartType !== undefined ? { chartType } : {}),
        ...(dataSource !== undefined ? { dataSource } : {}),
        ...(config !== undefined ? { config } : {}),
        ...(position !== undefined ? { position } : {}),
        ...(size !== undefined ? { size } : {}),
        ...(filters !== undefined ? { filters } : {}),
        ...(drillDownEnabled !== undefined ? { drillDownEnabled } : {}),
        ...(realTimeEnabled !== undefined ? { realTimeEnabled } : {}),
        ...(mlEnabled !== undefined ? { mlEnabled } : {}),
        ...(description !== undefined ? { description } : {}),
        ...(mlModel !== undefined ? { mlModel } : {}),
      };

      state.widgets[index] = updated;
    },

    deleteWidget: (state, action: PayloadAction<string>) => {
      state.widgets = state.widgets.filter((w) => w.id !== action.payload);
      if (state.selectedWidget?.id === action.payload) {
        state.selectedWidget = null;
      }
    },

    updateWidgetPosition: (
      state,
      action: PayloadAction<{ id: string; position: IWidgetPosition }>,
    ) => {
      const widget = state.widgets.find((w) => w.id === action.payload.id);
      if (widget) {
        widget.position = action.payload.position;
      }
    },

    // Источники данных
    setActiveDataSource: (state, action: PayloadAction<IDataSource | null>) => {
      state.activeDataSource = action.payload;
    },

    addDataSource: (state, action: PayloadAction<IDataSource>) => {
      state.dataSources.push(action.payload);
    },

    updateDataSource: (
      state,
      action: PayloadAction<Partial<IDataSource> & { id: string }>,
    ) => {
      const index = state.dataSources.findIndex(
        (ds) => ds.id === action.payload.id,
      );
      if (index === -1) return;

      const existing = state.dataSources[index];
      if (!existing) return;

      const {
        name,
        type,
        connection,
        query,
        parameters,
        refreshInterval,
        lastUpdated,
        schema,
        ...rest
      } = action.payload;

      const updated: IDataSource = {
        ...existing,
        ...rest,
        name: name ?? existing.name,
        ...(type !== undefined ? { type } : {}),
        ...(connection !== undefined ? { connection } : {}),
        ...(query !== undefined ? { query } : {}),
        ...(parameters !== undefined ? { parameters } : {}),
        ...(refreshInterval !== undefined ? { refreshInterval } : {}),
        ...(lastUpdated !== undefined ? { lastUpdated } : {}),
        ...(schema !== undefined ? { schema } : {}),
      };

      state.dataSources[index] = updated;
    },

    // Фильтры
    addGlobalFilter: (state, action: PayloadAction<IAnalyticsFilter>) => {
      state.globalFilters.push(action.payload);
    },

    updateGlobalFilter: (
      state,
      action: PayloadAction<Partial<IAnalyticsFilter> & { id: string }>,
    ) => {
      const index = state.globalFilters.findIndex(
        (f) => f.id === action.payload.id,
      );
      if (index === -1) return;

      const existing = state.globalFilters[index];
      if (!existing) return;

      const {
        value,
        field,
        operator,
        label,
        type,
        options,
        required,
        visible,
        ...rest
      } = action.payload;

      const updated: IAnalyticsFilter = {
        ...existing,
        ...rest,
        field: field ?? existing.field,
        ...(operator !== undefined ? { operator } : {}),
        ...(value !== undefined ? { value } : {}),
        ...(label !== undefined ? { label } : {}),
        ...(type !== undefined ? { type } : {}),
        ...(options !== undefined ? { options } : {}),
        ...(required !== undefined ? { required } : {}),
        ...(visible !== undefined ? { visible } : {}),
      };

      state.globalFilters[index] = updated;
    },

    removeGlobalFilter: (state, action: PayloadAction<string>) => {
      state.globalFilters = state.globalFilters.filter(
        (f) => f.id !== action.payload,
      );
    },

    setDateRange: (
      state,
      action: PayloadAction<{ start: Date; end: Date }>,
    ) => {
      state.dateRange = action.payload;
    },

    // Real-time данные
    updateRealTimeData: (
      state,
      action: PayloadAction<{ key: string; data: unknown }>,
    ) => {
      state.realTimeData[action.payload.key] = action.payload.data;
    },

    // Настройки
    updateSettings: (
      state,
      action: PayloadAction<Partial<AnalyticsState["settings"]>>,
    ) => {
      state.settings = { ...state.settings, ...action.payload };
    },

    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload;
    },

    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload;
    },

    // Очистка ошибок
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // Дашборды
    builder
      .addCase(fetchDashboards.pending, (state) => {
        state.loading.dashboards = true;
        state.error = null;
      })
      .addCase(fetchDashboards.fulfilled, (state, action) => {
        state.loading.dashboards = false;
        state.dashboards = action.payload;
      })
      .addCase(fetchDashboards.rejected, (state, action) => {
        state.loading.dashboards = false;
        state.error = action.error.message || "Ошибка загрузки дашбордов";
      })

      .addCase(createDashboard.pending, (state) => {
        state.loading.dashboards = true;
      })
      .addCase(createDashboard.fulfilled, (state, action) => {
        state.loading.dashboards = false;
        state.dashboards.push(action.payload);
        state.currentDashboard = action.payload;
      })
      .addCase(createDashboard.rejected, (state, action) => {
        state.loading.dashboards = false;
        state.error = action.error.message || "Ошибка создания дашборда";
      })

      // Виджеты
      .addCase(fetchWidgets.pending, (state) => {
        state.loading.widgets = true;
      })
      .addCase(fetchWidgets.fulfilled, (state, action) => {
        state.loading.widgets = false;
        state.widgets = action.payload;
      })
      .addCase(fetchWidgets.rejected, (state, action) => {
        state.loading.widgets = false;
        state.error = action.error.message || "Ошибка загрузки виджетов";
      })

      .addCase(createWidget.fulfilled, (state, action) => {
        state.widgets.push(action.payload);
      })

      // Метрики
      .addCase(fetchMetrics.pending, (state) => {
        state.loading.data = true;
      })
      .addCase(fetchMetrics.fulfilled, (state, action) => {
        state.loading.data = false;
        state.metrics = action.payload;
      })
      .addCase(fetchMetrics.rejected, (state, action) => {
        state.loading.data = false;
        state.error = action.error.message || "Ошибка загрузки метрик";
      })

      // Тренды дашборда (Q3)
      .addCase(fetchDashboardTrends.pending, (state) => {
        state.loadingTrends = true;
      })
      .addCase(fetchDashboardTrends.fulfilled, (state, action) => {
        state.loadingTrends = false;
        state.dashboardTrends = action.payload;
      })
      .addCase(fetchDashboardTrends.rejected, (state) => {
        state.loadingTrends = false;
      })

      // Аналитика пользователей
      .addCase(fetchUserAnalytics.pending, (state, action) => {
        state.loading.users = true;
        state.error = null;
        if (action.meta.arg) {
          state.usersAnalytics.filters = {
            ...state.usersAnalytics.filters,
            ...normalizeUserFilters(action.meta.arg),
          };
        }
      })
      .addCase(fetchUserAnalytics.fulfilled, (state, action) => {
        state.loading.users = false;
        state.usersAnalytics.items = action.payload.users;
        state.usersAnalytics.pagination = action.payload.pagination;
        state.usersAnalytics.filters = action.payload.filters;
      })
      .addCase(fetchUserAnalytics.rejected, (state, action) => {
        state.loading.users = false;
        state.error =
          action.error.message || "Ошибка загрузки аналитики пользователей";
      })

      .addCase(fetchUserMetrics.pending, (state) => {
        state.loading.data = true;
      })
      .addCase(fetchUserMetrics.fulfilled, (state, action) => {
        state.loading.data = false;
        state.usersAnalytics.metrics = action.payload;
      })
      .addCase(fetchUserMetrics.rejected, (state, action) => {
        state.loading.data = false;
        state.error =
          action.error.message || "Ошибка загрузки метрик пользователей";
      })

      .addCase(fetchUserSegmentation.pending, (state) => {
        state.loading.userSegmentation = true;
      })
      .addCase(fetchUserSegmentation.fulfilled, (state, action) => {
        state.loading.userSegmentation = false;
        state.usersAnalytics.segmentation = action.payload;
      })
      .addCase(fetchUserSegmentation.rejected, (state, action) => {
        state.loading.userSegmentation = false;
        state.error =
          action.error.message || "Ошибка загрузки сегментации пользователей";
      })

      // Отчеты
      .addCase(generateReport.pending, (state) => {
        state.generatingReport = true;
        state.loading.reports = true;
      })
      .addCase(generateReport.fulfilled, (state) => {
        state.generatingReport = false;
        state.loading.reports = false;
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.generatingReport = false;
        state.loading.reports = false;
        state.error = action.error.message || "Ошибка генерации отчета";
      });
  },
});

export const {
  setCurrentDashboard,
  updateDashboard,
  deleteDashboard,
  setSelectedWidget,
  updateWidget,
  deleteWidget,
  updateWidgetPosition,
  setActiveDataSource,
  addDataSource,
  updateDataSource,
  addGlobalFilter,
  updateGlobalFilter,
  removeGlobalFilter,
  setDateRange,
  updateRealTimeData,
  updateSettings,
  setAutoRefresh,
  setRefreshInterval,
  clearError,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
